import { z } from "zod";

/**
 * The WatchOver wire protocol.
 *
 * The SDK collects events on the client and ships them to the ingest collector
 * in *batches*. A batch carries the shared session context once (api key,
 * session id, visitor id, ...) and then a list of individual events. Individual
 * events use a compact `t` discriminator and only carry the fields unique to
 * them — everything common lives on the batch envelope.
 *
 * Replay (rrweb) events are large and self-describing, so they ride in their
 * own event variant (`replay`) that wraps an opaque array of rrweb records.
 * The ingest service never needs to understand rrweb internals; the dashboard
 * player does.
 */

// ── Primitives ──────────────────────────────────────────────────────────────

/** Epoch milliseconds. */
export const Timestamp = z.number().int().nonnegative();

/** Upper bounds on free-text wire fields. Attacker-controlled (any visitor can
 *  post them with the public key), so cap length to bound storage/DoS and keep
 *  values within their DB column sizes. Generous enough for legitimate data. */
export const STR = {
  url: 2048,
  text: 2048,
  selector: 1024,
  short: 512,
  message: 4096,
  stack: 12_000,
} as const;

/** Keys that must never appear in a stored property bag (prototype pollution
 *  defence for any downstream reader that reconstructs objects). */
const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/** Arbitrary, JSON-serialisable property bag attached to custom events. */
export const PropBag = z
  .record(
    z.string().max(256),
    z.union([z.string().max(STR.text), z.number(), z.boolean(), z.null()]),
  )
  .refine(
    (obj) => Object.keys(obj).every((k) => !FORBIDDEN_KEYS.has(k)),
    { message: "forbidden key in property bag" },
  );
export type PropBag = z.infer<typeof PropBag>;

// ── Individual events ─────────────────────────────────────────────────────────
// Each variant is discriminated by `t`. Keep payloads small: the SDK emits a lot
// of these, and replay frames dominate bandwidth already.

const Base = z.object({ t: z.string(), ts: Timestamp });

export const PageviewEvent = Base.extend({
  t: z.literal("pageview"),
  url: z.string().max(STR.url),
  path: z.string().max(STR.url),
  referrer: z.string().max(STR.url).optional(),
  title: z.string().max(STR.short).optional(),
});

export const NavigationEvent = Base.extend({
  t: z.literal("navigation"),
  from: z.string().max(STR.url),
  to: z.string().max(STR.url),
  /** "pushState" | "replaceState" | "popstate" | "hashchange" */
  kind: z.string().max(32),
});

export const ClickEvent = Base.extend({
  t: z.literal("click"),
  /** A stable-ish CSS selector for the clicked element. */
  selector: z.string().max(STR.selector),
  tag: z.string().max(32),
  /** Visible text of the target, already masked if the element is sensitive. */
  text: z.string().max(STR.text).optional(),
  x: z.number(),
  y: z.number(),
  /** Set by the worker, never the SDK; present here so the type is reusable. */
  button: z.number().optional(),
});

export const InputEvent = Base.extend({
  t: z.literal("input"),
  selector: z.string().max(STR.selector),
  tag: z.string().max(32),
  inputType: z.string().max(32).optional(),
  /** True when the value was redacted (sensitive field / masked selector). */
  masked: z.boolean(),
  /** Length of the value — kept even when masked, never the value itself. */
  length: z.number().int().nonnegative().optional(),
  /** Only present for non-masked fields the customer explicitly allow-listed. */
  value: z.string().max(STR.text).optional(),
});

export const ScrollEvent = Base.extend({
  t: z.literal("scroll"),
  x: z.number(),
  y: z.number(),
  /** 0..1 fraction of the document scrolled. */
  depth: z.number().min(0).max(1),
});

export const ViewportEvent = Base.extend({
  t: z.literal("viewport"),
  w: z.number().int().nonnegative(),
  h: z.number().int().nonnegative(),
});

export const CustomEvent = Base.extend({
  t: z.literal("custom"),
  name: z.string().min(1).max(120),
  props: PropBag.optional(),
});

export const IdentifyEvent = Base.extend({
  t: z.literal("identify"),
  userId: z.string().min(1).max(255),
  traits: PropBag.optional(),
});

export const ErrorEvent = Base.extend({
  t: z.literal("error"),
  message: z.string().max(STR.message),
  source: z.string().max(STR.url).optional(),
  line: z.number().optional(),
  col: z.number().optional(),
  stack: z.string().max(STR.stack).optional(),
});

export const PerformanceEvent = Base.extend({
  t: z.literal("performance"),
  /** Web-vital name: LCP | CLS | INP | FCP | TTFB | LOAD */
  metric: z.string().max(32),
  value: z.number(),
});

/** Max rrweb records carried in a single replay event. One SDK flush is capped
 *  at 200 records; this bounds a crafted payload well above that while keeping
 *  a single jsonb row and transaction from being unboundedly large. */
export const REPLAY_RECORDS_MAX = 5000;

export const ReplayEvent = Base.extend({
  t: z.literal("replay"),
  /** Opaque rrweb event records. Validated structurally, not semantically. */
  records: z.array(z.unknown()).max(REPLAY_RECORDS_MAX),
});

/** UTM + paid-click attribution parsed from the landing URL. */
export const Attribution = z.object({
  source: z.string().optional(),
  medium: z.string().optional(),
  campaign: z.string().optional(),
  term: z.string().optional(),
  content: z.string().optional(),
  referrer: z.string().optional(),
  landingPath: z.string().optional(),
  ts: Timestamp.optional(),
});
export type Attribution = z.infer<typeof Attribution>;

/**
 * One-per-session enrichment: where the visitor came from (UTM, click ids,
 * referrer, first- vs last-touch) and everything about their client we can read
 * without a permission prompt (screen, timezone, language, platform, network).
 * The ingest service folds this onto the session row.
 */
export const ContextEvent = Base.extend({
  t: z.literal("context"),
  // Acquisition (last touch = this session's landing).
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmTerm: z.string().optional(),
  utmContent: z.string().optional(),
  /** gclid / fbclid / msclkid / ttclid … */
  clickIds: z.record(z.string(), z.string()).optional(),
  referrer: z.string().optional(),
  referringDomain: z.string().optional(),
  landingPath: z.string().optional(),
  /** First time this visitor was ever seen acquired (persisted across sessions). */
  firstTouch: Attribution.optional(),
  // Client facets.
  screenW: z.number().int().optional(),
  screenH: z.number().int().optional(),
  dpr: z.number().optional(),
  language: z.string().optional(),
  languages: z.array(z.string()).optional(),
  timezone: z.string().optional(),
  tzOffset: z.number().optional(),
  platform: z.string().optional(),
  deviceMemory: z.number().optional(),
  cores: z.number().int().optional(),
  touch: z.boolean().optional(),
  colorScheme: z.string().optional(),
  connection: z
    .object({
      effectiveType: z.string().optional(),
      downlink: z.number().optional(),
      rtt: z.number().optional(),
      saveData: z.boolean().optional(),
    })
    .optional(),
});

export const WireEvent = z.discriminatedUnion("t", [
  PageviewEvent,
  NavigationEvent,
  ClickEvent,
  InputEvent,
  ScrollEvent,
  ViewportEvent,
  CustomEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
  ReplayEvent,
  ContextEvent,
]);
export type WireEvent = z.infer<typeof WireEvent>;
export type WireEventType = WireEvent["t"];

// ── Batch envelope ────────────────────────────────────────────────────────────

/** Stable per-page-load context, captured once when the SDK boots. */
export const SessionContext = z.object({
  /** Public, project-scoped API key (the "write key"). */
  apiKey: z.string().min(8),
  /** Session id (rotates after inactivity timeout). */
  sessionId: z.string().min(8),
  /** Stable visitor id (persisted in localStorage, survives sessions). */
  visitorId: z.string().min(8),
  /** Application user id once identify() is called, else null. */
  userId: z.string().nullable().optional(),
  /** SDK semver, for server-side compatibility handling. */
  sdk: z.string(),
  /** Page that produced this batch (helps when batches span navigations). */
  url: z.string().optional(),
});
export type SessionContext = z.infer<typeof SessionContext>;

export const Batch = SessionContext.extend({
  /** Monotonic batch sequence number within the session (gap detection).
   *  Capped so `seq * 1000 + subSeq` (replay chunk ordering) can't overflow an
   *  int4 column. 2M batches ≈ far beyond any real session's lifetime. */
  seq: z.number().int().nonnegative().max(2_000_000),
  /** Client clock at send time — used to correct clock skew server-side. */
  sentAt: Timestamp,
  events: z.array(WireEvent).max(5000),
});
export type Batch = z.infer<typeof Batch>;

// ── Ingest responses ──────────────────────────────────────────────────────────

export const IngestOk = z.object({
  ok: z.literal(true),
  accepted: z.number().int().nonnegative(),
  /** Server-assigned session id if the client one was rejected/rotated. */
  sessionId: z.string().optional(),
});

export const IngestError = z.object({
  ok: z.literal(false),
  error: z.string(),
  /** When true the SDK should back off and retry the same batch later. */
  retryable: z.boolean(),
});

export type IngestResponse =
  | z.infer<typeof IngestOk>
  | z.infer<typeof IngestError>;
